import { Container } from '@acies/core';
import { DropdownEvent, InputField, InputFieldEvent, Spacer } from '@acies/ui';
import { ModelPosition, StockPosition, type TextPosition } from '../../../domain/positions';
import type { SearchResult } from '../../../domain/search-results';
import { searchConstructionModels } from '../../../services/construction-models';
import { searchInventoryItems } from '../../../services/inventory-items';
import { ConfiguratorModel } from '../configurator.model';
import SearchAutoComplete from '../shared/search-auto-complete/search-auto-complete';
import SearchAutoCompleteEvent from '../shared/search-auto-complete/search-auto-complete-event';
import CreatePositionRendererEvent from './create-position-renderer-event';
import ModelTypeDropdown from './model-type-dropdown';
import { toCountable, toPositionNumber } from './position-field-values';

export default class CreatePositionRenderer extends Container {
	#checkSpacer!: Spacer;
	#countInputField!: InputField;
	#descriptionSpacer!: Spacer;
	#heightInputField!: InputField;
	#litraInputField!: InputField;
	#menuSpacer!: Spacer;
	#modelTypeDropdown!: ModelTypeDropdown;
	#noteSpacer!: Spacer;
	#positionInputField!: InputField;
	#priceSpacer!: Spacer;
	#searchModelAutoComplete!: SearchAutoComplete<SearchResult>;
	#statusSpacer!: Spacer;
	#widthInputField!: InputField;
	public constructor() {
		super();
		this.width = 'fill';
		this.height = 32;
		this.gapHorizontal = 16;
		this.paddingHorizontal = 16;
		this.alignment = 'left';
	}

	public get checkSpacer(): Spacer {
		if (!this.#checkSpacer) {
			this.#checkSpacer = new Spacer(15, 15);
		}
		return this.#checkSpacer;
	}

	public get positionInputField(): InputField {
		if (!this.#positionInputField) {
			this.#positionInputField = new InputField();
			// where a position sits is a number, so the field takes digits and one dot and nothing
			// else. How small that number may be is not something typing can enforce — 0 is on the
			// way to 0.5 as much as to 42 — so the row judges the value it ends up holding
			this.#positionInputField.type = 'numeric';
			this.#positionInputField.negatives = false;
			this.#positionInputField.width = 56;
			this.#positionInputField.blurOnEnter = false;
		}
		return this.#positionInputField;
	}

	public get modelTypeDropdown(): ModelTypeDropdown {
		if (!this.#modelTypeDropdown) {
			this.#modelTypeDropdown = new ModelTypeDropdown();
		}
		return this.#modelTypeDropdown;
	}

	public get searchModelAutoComplete(): SearchAutoComplete<SearchResult> {
		if (!this.#searchModelAutoComplete) {
			this.#searchModelAutoComplete = new SearchAutoComplete<SearchResult>();
			// what this row searches while it is a model position, which it starts as: the store's
			// construction models, at the path the account's default engine names. The picker itself
			// knows nothing about stores or accounts — it asks this as the user types and shows what
			// comes back. Picking another type swaps it, see #fitToPositionType
			this.#searchModelAutoComplete.search = searchConstructionModels;
			// the header's own word for the column, on every field the row has one for: an empty row
			// is what the user meets, and the header scrolls out of sight above a long list
			this.#searchModelAutoComplete.placeholder = 'Nøgle';
			this.#searchModelAutoComplete.width = 192;
			// Enter creates the position from any field of the row, and a row it refuses keeps the user
			// in the field they pressed it in — see #entered
			this.#searchModelAutoComplete.inputField.blurOnEnter = false;
		}
		return this.#searchModelAutoComplete;
	}

	public get descriptionSpacer(): Spacer {
		if (!this.#descriptionSpacer) {
			this.#descriptionSpacer = new Spacer();
			this.#descriptionSpacer.minWidth = 88;
			this.#descriptionSpacer.width = 'fill';
		}
		return this.#descriptionSpacer;
	}

	public get heightInputField(): InputField {
		if (!this.#heightInputField) {
			this.#heightInputField = new InputField();
			this.#heightInputField.placeholder = 'Højde';
			this.#heightInputField.type = 'numeric';
			this.#heightInputField.decimals = false;
			this.#heightInputField.negatives = false;
			// a measurement is read against the ones above it, so the digits end where the rows above
			// end — which is also where the header sits. Height, width and count are the three columns
			// the header right aligns, and the only three this row does
			this.#heightInputField.textAlign = 'right';
			this.#heightInputField.width = 72;
			this.#heightInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#heightInputField.visibility = false;
		}
		return this.#heightInputField;
	}

	public get widthInputField(): InputField {
		if (!this.#widthInputField) {
			this.#widthInputField = new InputField();
			this.#widthInputField.placeholder = 'Bredde';
			this.#widthInputField.type = 'numeric';
			this.#widthInputField.decimals = false;
			this.#widthInputField.negatives = false;
			this.#widthInputField.textAlign = 'right';
			this.#widthInputField.width = 72;
			this.#widthInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#widthInputField.visibility = false;
		}
		return this.#widthInputField;
	}

	public get countInputField(): InputField {
		if (!this.#countInputField) {
			this.#countInputField = new InputField();
			this.#countInputField.placeholder = 'Antal';
			this.#countInputField.textAlign = 'right';
			this.#countInputField.width = 56;
			this.#countInputField.type = 'numeric';
			this.#countInputField.decimals = false;
			this.#countInputField.negatives = false;
			this.#countInputField.blurOnEnter = false;
		}
		return this.#countInputField;
	}

	public get litraInputField(): InputField {
		if (!this.#litraInputField) {
			this.#litraInputField = new InputField();
			this.#litraInputField.placeholder = 'Litra';
			this.#litraInputField.width = 128;
			this.#litraInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#litraInputField.visibility = false;
		}
		return this.#litraInputField;
	}

	public get noteSpacer(): Spacer {
		if (!this.#noteSpacer) {
			this.#noteSpacer = new Spacer();
			this.#noteSpacer.width = 48;
		}
		return this.#noteSpacer;
	}

	public get statusSpacer(): Spacer {
		if (!this.#statusSpacer) {
			this.#statusSpacer = new Spacer();
			this.#statusSpacer.width = 48;
		}
		return this.#statusSpacer;
	}

	public get priceSpacer(): Spacer {
		if (!this.#priceSpacer) {
			this.#priceSpacer = new Spacer();
			this.#priceSpacer.width = 82;
		}
		return this.#priceSpacer;
	}

	/**
	 * The room the rows of the list give their menu, kept empty here.
	 *
	 * Where the create button stood, and as wide as it was, so the row's columns stay where they were now that Enter creates the position instead.
	 */
	public get menuSpacer(): Spacer {
		if (!this.#menuSpacer) {
			this.#menuSpacer = new Spacer();
			this.#menuSpacer.width = 32;
		}
		return this.#menuSpacer;
	}

	protected override initialize(): void {
		super.initialize();
		// before the children, so the DropdownEvent the model type raises as it picks its first item
		// is heard: it is dispatched while the dropdown is being added, not after
		this.#addEventListeners();
		this.addComponents([this.checkSpacer, this.positionInputField, this.modelTypeDropdown, this.searchModelAutoComplete, this.descriptionSpacer, this.widthInputField, this.heightInputField, this.countInputField, this.litraInputField, this.noteSpacer, this.statusSpacer, this.priceSpacer, this.menuSpacer]);
		// the row is where a new position is typed, so it starts ready to be typed into: the place
		// in the list is filled in for the user, the count is one until they say otherwise, and the
		// model key is what they enter first
		this.positionInputField.text = `${this.#nextPositionNumber}`;
		this.countInputField.text = defaultQuantity;
		this.searchModelAutoComplete.focus();
	}

	/**
	 * One listener on this renderer catches every way the row can change.
	 *
	 * Every one of these events bubbles, so no child needs a listener of its own. Listening here also means no listener needs binding: `this` is the currentTarget, which is this renderer.
	 */
	#addEventListeners(): void {
		this.addEventListener(DropdownEvent.SELECTED_CHANGED, this.#positionTypeChanged as EventListener);
		this.addEventListener(InputFieldEvent.BLURRED, this.#fieldBlurred as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, this.#entered as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.BLURRED, this.#searchChanged as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.VALUE_CHANGED, this.#searchChanged as EventListener);
	}

	/** Every field of the row that can be marked as holding a value it cannot be created with. The litra is not among them, since a position may have none. */
	get #fields(): ReadonlyArray<InputField | SearchAutoComplete<SearchResult>> {
		return [this.positionInputField, this.searchModelAutoComplete, this.widthInputField, this.heightInputField, this.countInputField];
	}

	/**
	 * The fields holding a value the row cannot be created with, or none when it can be.
	 *
	 * The litra is left out on purpose — a position may have none. Everything else has to be there before there is a position to create.
	 *
	 * The model is judged by what the search field holds rather than by what was picked in it. The two are the same once a model is picked, since picking one writes its key into the field, but someone who knows the key can type it and move on without ever opening the popover.
	 */
	get #invalidFields(): ReadonlyArray<InputField | SearchAutoComplete<SearchResult>> {
		const invalid: Array<InputField | SearchAutoComplete<SearchResult>> = [];
		if (!isPositionNumber(this.positionInputField.text) || this.#isPositionNumberTaken) {
			invalid.push(this.positionInputField);
		}
		if (this.searchModelAutoComplete.text === '') {
			invalid.push(this.searchModelAutoComplete);
		}
		// a model position is the only kind that is made to a size, so the two measurements are only
		// asked for while the row is one — which is also why they are hidden for the other types
		if (this.#isModelPosition && !isCountable(this.widthInputField.text)) {
			invalid.push(this.widthInputField);
		}
		if (this.#isModelPosition && !isCountable(this.heightInputField.text)) {
			invalid.push(this.heightInputField);
		}
		if (!isCountable(this.countInputField.text)) {
			invalid.push(this.countInputField);
		}
		return invalid;
	}

	/**
	 * Whether the place typed into the row is already taken by a position in the list.
	 *
	 * An empty field reads as 0, which no position can be, so a row that has not been filled in is not reported as a clash.
	 */
	get #isPositionNumberTaken(): boolean {
		const typed = Number(this.positionInputField.text);
		return ConfiguratorModel.positions.collection.some((position) => position.position === typed);
	}

	/**
	 * The place a new position takes: the first one free after the last one in the list.
	 *
	 * Whole even when the position it follows is not, so a row after 7.1 is offered 8. An empty list falls out of the same sum, since the highest of nothing is 0 and the first place is 1.
	 */
	get #nextPositionNumber(): number {
		const highest = ConfiguratorModel.positions.collection.reduce((max, position) => Math.max(max, position.position), 0);
		return Math.floor(highest) + 1;
	}

	/** Whether the row is being filled in as a model position, the one type measured by size. */
	get #isModelPosition(): boolean {
		return this.#selectedPositionType === modelPositionType;
	}

	/** Whether the row is being filled in as a stock position, the one type whose key is an inventory item. */
	get #isStockPosition(): boolean {
		return this.#selectedPositionType === stockPositionType;
	}

	/** The kind of position the row is being filled in as, or null while no type is picked. */
	get #selectedPositionType(): unknown {
		const { selectedItem } = this.modelTypeDropdown;
		return selectedItem === null ? null : selectedItem.value;
	}

	/**
	 * Creates the position the row has been filled in with, when Enter is pressed in any of its fields.
	 *
	 * The Enter is stopped here and a CreatePositionRendererEvent raised in its place, so what leaves the row is a position rather than a key pressed in one of its fields.
	 *
	 * A row that does not read as a whole position is refused, and every field holding a value it cannot be created with is marked as an error. Nothing is dispatched, and the focus stays in the field Enter was pressed in — no field of the row lets go of it on Enter — so the user goes on from where they were. A mark
	 * stays until its field is focused again — which an InputField answers by taking its own mark off, the search field's included — or the type is changed, see #positionTypeChanged, rather than going the moment the value is mended: it says what was wrong when the row was refused.
	 *
	 * No type picked is a row refused with no field to mark, since the dropdown has no error of its own to show.
	 */
	#entered(event: InputFieldEvent): void {
		event.stopPropagation();
		for (const field of this.#invalidFields) {
			field.error = true;
		}
		this.#create();
	}

	/** Raises the position the row reads as, and empties the row for the next one — or does nothing, for a row that reads as none. */
	#create(): void {
		// asked whether or not a field was marked: it refuses the same rows the marks are put on, and
		// it is the one that knows what the fields read as
		const position = this.#getNewPositionFromInput();
		if (position !== null) {
			this.dispatchEvent(new CreatePositionRendererEvent(position));
			this.#reset();
			// the emptied row is a new one, and a new row is ready to be typed into
			this.searchModelAutoComplete.focus();
		}
	}

	/**
	 * Marks the place as taken when the user leaves it holding one another position already has.
	 *
	 * Only when the place itself was left: leaving another field says nothing new about whether that place is taken. Only ever setting the mark and never taking it off, for the reason #entered gives: it goes when the field is focused again, which is the way back to mending it — so a place left free still carries a
	 * mark Enter put there.
	 *
	 * The event is stopped here: a change inside the row is this renderer's business, and nothing above it in the DOM has any use for it.
	 */
	#fieldBlurred(event: InputFieldEvent): void {
		event.stopPropagation();
		if (event.target === this.positionInputField && this.#isPositionNumberTaken) {
			this.positionInputField.error = true;
		}
	}

	/**
	 * Fits the row to the type the user picked, and takes every mark off it.
	 *
	 * A mark said what was wrong with the row as the type it was refused as, and the fields asked for are not the same for another: a model position is measured and a stock position is not.
	 */
	#positionTypeChanged(event: DropdownEvent): void {
		event.stopPropagation();
		this.#clearErrors();
		this.#fitToPositionType();
	}

	/**
	 * Stops the search field saying it has been left or that its key has settled, which the row has no use for: the field is read when Enter is pressed rather than judged as it changes.
	 *
	 * Stopped all the same, since it has been dealt with here and nothing above the row is meant to hear it.
	 */
	#searchChanged(event: SearchAutoCompleteEvent): void {
		event.stopPropagation();
	}

	/** Takes the mark off every field of the row. */
	#clearErrors(): void {
		for (const field of this.#fields) {
			field.error = false;
		}
	}

	/**
	 * Reads the row as the position it describes, or null when it describes none.
	 *
	 * Null rather than a thrown error, because a half-filled row is an ordinary state rather than a fault. The rules are the ones #invalidFields marks the fields by, read here off the values themselves: it is these values the position is built from, and a schema class refuses anything it cannot hold.
	 *
	 * A text position is never built yet: it is the one kind whose own field, the text it shows, the row has nowhere to type.
	 */
	#getNewPositionFromInput(): ModelPosition | StockPosition | TextPosition | null {
		// what the search field holds, which is the picked model's key when one was picked and a
		// key typed straight in when none was
		const key = this.searchModelAutoComplete.text;
		const quantity = toCountable(this.countInputField.text);
		// a place another position holds is no place for this one
		const position = this.#isPositionNumberTaken ? null : toPositionNumber(this.positionInputField.text);
		if (key === '' || quantity === null || position === null) {
			return null;
		}
		const height = toCountable(this.heightInputField.text);
		const shared = { ...positionDefaults, key, position };
		const width = toCountable(this.widthInputField.text);
		if (this.#isModelPosition) {
			if (height === null || width === null) {
				return null;
			}
			return new ModelPosition({
				...shared,
				quantity,
				height,
				litra: toNullOrText(this.litraInputField.text),
				note: null,
				type: modelPositionType,
				width,
			});
		}
		if (this.#isStockPosition) {
			// the configuration the item comes in is picked once the position is in the list, among the
			// ones the engine answers its first configuration with — see `StockConfiguration`
			return new StockPosition({ ...shared, quantity, type: stockPositionType });
		}
		return null;
	}

	/**
	 * Empties the row, so the next position can be typed into it straight away.
	 *
	 * The fields are cleared rather than replaced, so the row keeps its place in the list and the user keeps their cursor. Fitting the row to its type is done here at the end rather than left to the listeners: none of these fire for a value the row sets on itself, and the one that does — the dropdown, which raises a
	 * DropdownEvent when its selection goes — does so partway through.
	 */
	#reset(): void {
		// the count goes back to one rather than to nothing, as the row started
		this.countInputField.text = defaultQuantity;
		this.heightInputField.text = '';
		this.litraInputField.text = '';
		this.modelTypeDropdown.reset();
		// the position that was just created took a place, so the emptied row is offered the next
		this.positionInputField.text = `${this.#nextPositionNumber}`;
		this.searchModelAutoComplete.reset();
		this.widthInputField.text = '';
		this.#clearErrors();
		this.#fitToPositionType();
	}

	/**
	 * Fits the row to the kind of position it is being filled in as: which fields it shows, and what its key is searched in.
	 *
	 * The height, the width and the litra show only while the row is a model position. A stock and a text position have none of the three, so for those the fields have nothing to hold, and a litra typed into one would be dropped without a word. They are hidden rather than removed, so the row's columns stay lined up
	 * with the header whichever type is picked.
	 *
	 * The key is searched for among the store's inventory items while the row is a stock position, by item number, and among its construction models otherwise. Swapping the search leaves the key typed so far where it is: it is read again on the next keystroke, against the collection the type now names.
	 *
	 * The configuration a stock position's item comes in is not picked here: it is picked in the answers section once the position is in the list — see `StockConfiguration`.
	 *
	 * The count stays whatever the type: a model and a stock position are both counted.
	 */
	#fitToPositionType(): void {
		const isModelPosition = this.#isModelPosition;
		this.heightInputField.visibility = isModelPosition;
		this.litraInputField.visibility = isModelPosition;
		this.widthInputField.visibility = isModelPosition;
		this.searchModelAutoComplete.search = this.#isStockPosition ? searchInventoryItems : searchConstructionModels;
	}
}

/** The values ModelTypeDropdown offers each kind under, which are what `type` names them by too. */
const modelPositionType = 'model';
const stockPositionType = 'stock';

/** The count a row starts with: one of whatever it is, which is what most positions are, so a model needs only its two measurements before Enter creates it. */
const defaultQuantity = '1';

/**
 * What a freshly filled in row cannot say for itself.
 *
 * The API prices the position once it has it, so the row hands over every position free and uncalculated. Where it lands in the list the row does know: that is typed into it.
 */
const positionDefaults = {
	description: null,
	price: 0,
	status: 'pending',
} as const;

/** Whether what the user typed reads as a quantity, a height or a width. See `toCountable`. */
function isCountable(text: string): boolean {
	return toCountable(text) !== null;
}

/** Whether what the user typed reads as a place in the list. See `toPositionNumber`. */
function isPositionNumber(text: string): boolean {
	return toPositionNumber(text) !== null;
}

/** What an optional text field holds, or null when it holds nothing: no field is an empty string. */
function toNullOrText(text: string): string | null {
	return text === '' ? null : text;
}

customElements.define('cf-create-position-renderer', CreatePositionRenderer);
